import React, { useEffect, useState } from "react";
import api from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EXPENSE_CATEGORIES, PAYMENT_METHODS, fmtDate, inr, todayISO } from "@/lib/tps";
import DatePicker from "@/components/DatePicker";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const blank = {
  expense_date: todayISO(), category: "Ingredients", description: "",
  amount: 0, payment_method: "Cash", notes: "",
};

export default function Expenses() {
  const { isOwner } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);

  const load = () => api.get("/expenses").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.expense_date || !form.amount) { toast.error("Date and amount required"); return; }
    try {
      await api.post("/expenses", { ...form, amount: parseFloat(form.amount) });
      toast.success("Expense added");
      setOpen(false); setForm(blank); load();
    } catch (err) { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try { await api.delete(`/expenses/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
  };

  const total = rows.reduce((s, r) => s + (r.amount || 0), 0);

  return (
    <div className="space-y-3 animate-fade-in" data-testid="expenses-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl">Expenses</h1>
          <div className="text-sm text-[#4B5563]">Total: <span className="font-medium text-[#141E30]">{inr(total)}</span></div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#B86F77] hover:bg-[#A35D65] h-9" data-testid="add-expense-btn"><Plus className="h-4 w-4 mr-1" /> Add Expense</Button>
          </DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader><DialogTitle>Add Expense</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Date</Label>
                  <DatePicker value={form.expense_date} onChange={(v) => update("expense_date", v)} testId="exp-date" />
                </div>
                <div>
                  <Label className="text-xs">Amount (₹)</Label>
                  <Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => update("amount", e.target.value)} className="h-8" data-testid="exp-amount" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Category</Label>
                  <Select value={form.category} onValueChange={(v) => update("category", v)}>
                    <SelectTrigger className="h-8" data-testid="exp-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{EXPENSE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Payment Method</Label>
                  <Select value={form.payment_method} onValueChange={(v) => update("payment_method", v)}>
                    <SelectTrigger className="h-8" data-testid="exp-method"><SelectValue /></SelectTrigger>
                    <SelectContent>{PAYMENT_METHODS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Input value={form.description} onChange={(e) => update("description", e.target.value)} className="h-8" data-testid="exp-desc" />
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => update("notes", e.target.value)} data-testid="exp-notes" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="h-8 border-[#E8E3DF]" data-testid="exp-cancel">Cancel</Button>
              <Button onClick={save} className="h-8 bg-[#B86F77] hover:bg-[#A35D65]" data-testid="exp-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-[#E8E3DF] overflow-hidden">
        <div className="overflow-x-auto">
          <Table className="tps-table">
            <TableHeader className="bg-[#FCF9F9]">
              <TableRow>
                <TableHead className="py-2">Date</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Method</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                {isOwner && <TableHead></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-[#4B5563]">No expenses yet.</TableCell></TableRow>}
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`exp-row-${r.id}`}>
                  <TableCell className="py-2">{fmtDate(r.expense_date)}</TableCell>
                  <TableCell className="py-2 text-sm">{r.category}</TableCell>
                  <TableCell className="py-2 text-sm">{r.description || "—"}</TableCell>
                  <TableCell className="py-2 text-sm">{r.payment_method}</TableCell>
                  <TableCell className="py-2 text-right text-sm font-medium">{inr(r.amount)}</TableCell>
                  {isOwner && (
                    <TableCell className="py-2 text-right">
                      <button onClick={() => remove(r.id)} className="text-rose-600 hover:bg-rose-50 p-1 rounded" data-testid={`exp-del-${r.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
